import type { MouseEvent, ReactElement } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Users as UsersIcon } from 'lucide-react';

interface ClickableUserCountProps {
  count: number;
  /** Short phrase describing the scope, e.g. "with this plugin" or "on this version". */
  tooltipScope: string;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Inline user-count cell with a tooltip "Click to see user list" affordance.
 * Shared between the plugin-level row (PluginsTable) and the per-version row
 * (PluginsTab) so the two stay visually and behaviorally in sync.
 */
export function ClickableUserCount({
  count,
  tooltipScope,
  onClick,
}: ClickableUserCountProps): ReactElement {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            className="inline-flex items-center gap-1.5 font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors cursor-pointer"
          >
            <UsersIcon className="h-3.5 w-3.5" />
            {count}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <UsersIcon className="h-3 w-3" />
              <span>
                {count} user{count !== 1 ? 's' : ''} {tooltipScope}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Click to see user list
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
